<template>
  <section id="member-orders">
    <div class="section-container">
      <div class="a-header-container">
        <div class="page-label">
          Member Orders
        </div>
        <div class="a-right-container">
          <button
            type="button"
            class="export-button"
            @click="exportMembersCSV"
          >
            <span v-if="!exporting">
              EXPORT CSV
              <img
                src="@shared/assets/img/general/export-download.png"
                alt="Download"
              >
            </span>
            <span v-else>
              EXPORTING
              <img
                src="@shared/assets/img/general/loading-white.svg"
                alt="Exporting"
              >
            </span>
          </button>
          <div class="search-container">
            <SACSearch
              darkMode
              :width="200"
              :height="40"
              :search="search"
              placeholder="Search"
              @input="(value) => updateSearch(value.trim())"
            />
          </div>
          <SelectedPartner />
        </div>
      </div>
      <div class="body-container">
        <MemberOrdersFilters
          :filters="orderFilters"
          :change="updateFilters"
          :clear="clearFilter"
          :toggle="handleChange"
          :loading="loading"
          :disabled="orderFilters.length === 0"
        />
        <MemberOrdersBoard :orderTableSearch="search" />
      </div>
    </div>
    <Modal />
    <AllMemberOrdersFilterOptions
      :show="showRightSidebar"
      :data="orderFilters.filter(d => d.showOnAll)"
      :update="updateFilters"
    />
  </section>
</template>

<script>
import adminMixin from '@shared/mixins/adminMixin';
import orderMixin from '@shared/mixins/orderMixin';

import MemberOrdersBoard from '@shared/components/memberOrders/MemberOrdersBoard.vue';

import apiClient from '@shared/lib/general/apiClient';
import exportToFile from '@shared/lib/util/exportToFile';
import SACSearch from '@shared/components/sac/SACSearch.vue';
import MemberOrdersFilters from '@shared/components/memberOrders/memberOrdersFilter/MemberOrdersFilters.vue';
import SelectedPartner from '@shared/components/admin/SelectedPartner.vue';
import AllMemberOrdersFilterOptions from '@shared/components/memberOrders/memberOrdersFilter/AllMemberOrdersFilterOptions.vue';
import Modal from '@shared/components/order/Modal.vue';

export default {
  name: 'MemberOrdersView',
  metaInfo: {
    title: 'SmartAC.com | Member Orders',
  },
  components: {
    SelectedPartner,
    SACSearch,
    MemberOrdersBoard,
    MemberOrdersFilters,
    Modal,
    AllMemberOrdersFilterOptions,
  },
  mixins: [adminMixin, orderMixin],
  data() {
    return {
      search: '',
      showSearch: false,
      installations: [],
      exporting: false,
    };
  },
  watch: {
    selectedCorpId() {
      if (!this.loading) {
        this.initialLoad();
      }
    },
  },
  mounted() {
    this.initialLoad();
  },
  methods: {
    async initialLoad() {
      await Promise.all([
        this.$store.dispatch('filterMemberOrdersCustomer'),
        this.$store.dispatch('getAllProductLine'),
        this.$store.dispatch('getAllDiscountCodes'),
        this.getTechnicians(),
      ]);
    },
    updateSearch(value) {
      this.search = value;
    },
    handleChange(field, value) {
      this.$store.dispatch('changeOrderValues', [{
        field,
        value,
      }]);
    },
    async updateFilters(filters) {
      await this.$store.dispatch('changeOrderValues', [{
        field: 'orderFilters',
        value: filters,
      }]);
      await this.$store.dispatch('filterMemberOrdersCustomer');
    },
    clearFilter(key) {
      const newFilters = this.orderFilters.map((filter) => {
        if (filter.key === key && key !== 'orderDate') {
          filter.active = false;
          filter.activeOption = {};
        } else if (filter.key === key && key === 'orderDate') {
          filter.active = false;
          filter.activeOption = {};
          filter.options[0].value = null;
        }
        return filter;
      });
      this.updateFilters(newFilters);
    },
    async getTechnicians(corpId, techId) {
      try {
        const { data } = await apiClient({
          method: 'GET',
          url: `/technicians/all/${this.selectedCorpId}`,
        });
        const newFilters = this.orderFilters.map((filter) => {
          const newFilter = {
            ...filter,
          };
          if (filter.key === 'techs') {
            let activeOption = {};
            const techOptions = data.map((tech) => {
              const option = {
                label: `${tech.firstName} ${tech.lastName}`,
                value: tech.id,
              };
              return option;
            });
            newFilter.options = techOptions.sort((a, b) => {
              if (a.label.toLowerCase() > b.label.toLowerCase()) {
                return 1;
              }
              if (a.label.toLowerCase() < b.label.toLowerCase()) {
                return -1;
              }
              return 0;
            });
            if (techId) {
              activeOption = techOptions.find((option) => Number(option.value) === Number(techId));
            }
            if (activeOption.value) {
              newFilter.activeOption = activeOption;
              newFilter.active = true;
            }
          }
          return newFilter;
        });
        this.updateFilters(newFilters);
      } catch (err) {
        console.error(err);
      }
    },
    async exportMembersCSV() {
      this.exporting = true;
      try {
        const {
          admin: {
            adminFilter: {
              corpId,
            },
          },
        } = this.$store.state;
        const url = this.formMembersUrl(`customer/orders/export/corp/${corpId}`);
        const { data } = await apiClient({
          method: 'GET',
          url,
        });
        exportToFile({
          data,
          filename: 'Member_Order_Details.csv',
        });
      } catch (err) {
        console.error(err);
      }
      this.exporting = false;
    },
    formMembersUrl(baseUrl) {
      const {
        orderFilters,
      } = this;
      let url = baseUrl;
      orderFilters.forEach((filter) => {
        const {
          active,
          key,
          activeOption: {
            value,
          },
        } = filter;
        if (active) {
          if (!url.includes('?')) {
            url += '?';
          } else {
            url += '&';
          }
          if (key === 'techs') {
            url += `techId=${value}`;
          }
        }
      });
      return url;
    },
  },
};
</script>

<style lang="scss" scoped>
section#member-orders {
  display: flex;
  flex: 1;
  background: $BLACK;
  letter-spacing: 0.2px;
  justify-content: center;
  padding: 52px 100px 36px;

  @media (max-width: 1279px) {
    padding: 52px 60px 0;
  }

  @media (max-width: 1023px) {
    padding: 52px 40px 0;
  }

  .section-container {
    display: flex;
    flex-direction: column;
    max-width: 1705px;
    width: 100%;

    .back-container {
      font-weight: 700;
      line-height: 1;
      color: rgba(139, 169, 255, 1);
      display: flex;
      align-items: center;
    }
    .a-header-container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-bottom: 10px;

      .a-right-container {
        display: flex;
        align-items: center;
        gap: 16px;

        .export-button {
          height: 38px;
          padding: 0 8px;
          color: $N50;
          display: flex;
          align-items: center;
          white-space: nowrap;
          font-size: 0.875rem;
          font-weight: 700;
          border: 1px solid $N50;
          border-radius: 8px;
          line-height: 1;

          span {
            height: 100%;
            display: flex;
            align-items: center;
          }

          img {
            height: 40%;
            margin-left: 8px;
            filter: invert(50%);
          }
        }
      }
    }
  }
}
</style>
